import React, { useEffect, useMemo, useState } from "react";
import { Badge, Card, CardBody, CardHeader, Container, Table } from "reactstrap";

import AdminListControls from "components/admin/AdminListControls";
import AdminPageHeader from "components/admin/AdminPageHeader";
import AdminStateBlock from "components/admin/AdminStateBlock";
import { adminThemes } from "data/admin/domain";
import { themeLabel } from "labels/tr";
import { provincesRepo } from "data/repositories/provincesRepo";
import { alertsRepo } from "data/repositories/alertsRepo";

const sortOptions = [
  { value: "created-desc", label: "Yeni eklenen" },
  { value: "province-asc", label: "Şehre göre" },
];

export default function AdminAlerts() {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [provinceFilter, setProvinceFilter] = useState("");
  const [themeFilter, setThemeFilter] = useState("");
  const [sortBy, setSortBy] = useState("created-desc");

  useEffect(() => {
    try {
      setAlerts(alertsRepo.list());
    } catch (loadError) {
      setError(loadError?.message || "Uyarı kayıtları yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, []);

  const provinceOptions = useMemo(
    () => [{ value: "", label: "Tüm şehirler" }, ...provincesRepo.list().map((province) => ({ value: province.id, label: province.name }))],
    []
  );

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    const next = alerts.filter((alert) => {
      if (provinceFilter && alert.provinceId !== provinceFilter) return false;
      if (themeFilter && alert.theme !== themeFilter) return false;
      if (!query) return true;
      return [alert.provinceId, alert.theme, alert.dateFrom, alert.dateTo, alert.title].join(" ").toLowerCase().includes(query);
    });

    return [...next].sort((left, right) => {
      if (sortBy === "province-asc") return String(left.provinceId || "").localeCompare(String(right.provinceId || ""), "tr");
      return String(right.createdAt || "").localeCompare(String(left.createdAt || ""));
    });
  }, [alerts, provinceFilter, search, sortBy, themeFilter]);

  const resetFilters = () => {
    setSearch("");
    setProvinceFilter("");
    setThemeFilter("");
    setSortBy("created-desc");
  };

  return (
    <>
      <AdminPageHeader title="Yönetim — Uyarı Yönetimi" description="Kalıcı uyarı kuralları yönetici tarafından görüntülenir; bu ekran yönetim görünürlüğüne odaklanır." />
      <Container className="mt--6" fluid>
        <Card className="shadow">
          <CardHeader>
            <h3 className="mb-3">Kalıcı uyarı kuralları</h3>
            <AdminListControls
              search={search}
              onSearch={setSearch}
              searchPlaceholder="Şehir, tema veya tarih ara"
              filters={[
                { key: "province", label: "Şehir", value: provinceFilter, onChange: setProvinceFilter, options: provinceOptions },
                { key: "theme", label: "Tema", value: themeFilter, onChange: setThemeFilter, options: [{ value: "", label: "Tüm temalar" }, ...adminThemes.map((theme) => ({ value: theme, label: themeLabel(theme) }))] },
              ]}
              sortValue={sortBy}
              onSortChange={setSortBy}
              sortOptions={sortOptions}
              onReset={resetFilters}
              resultCount={filtered.length}
              totalCount={alerts.length}
            />
          </CardHeader>
          <CardBody>
            <AdminStateBlock
              error={error}
              onDismissError={() => setError("")}
              loading={loading}
              spinnerColor="dark"
              isEmpty={!filtered.length}
              emptyTitle="Uyarı kaydı yok"
            >
              <Table responsive>
                <thead className="thead-light">
                  <tr>
                    <th>ID</th>
                    <th>Şehir</th>
                    <th>Tema</th>
                    <th>Tarih aralığı</th>
                    <th>Oluşturulma</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((alert) => (
                    <tr key={alert.id}>
                      <td><small>{alert.id}</small></td>
                      <td>{alert.provinceId || "Tümü"}</td>
                      <td>{alert.theme ? <Badge color="info" pill>{themeLabel(alert.theme)}</Badge> : "Tümü"}</td>
                      <td>{alert.dateFrom || "-"} / {alert.dateTo || "-"}</td>
                      <td>{alert.createdAt || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </AdminStateBlock>
          </CardBody>
        </Card>
      </Container>
    </>
  );
}
